import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { useFY } from "../../context/FYContext";
import { useCompany } from "../../context/CompanyContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Sun, Moon, LogOut, User, Search, ChevronRight, ClipboardCheck, Calendar, GraduationCap, Menu, Keyboard, Building2, Database } from "lucide-react";
import { Button } from "../ui/button";
import api from "../../lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import GlobalSearch from "./GlobalSearch";
import NotificationBell from "./NotificationBell";
import VoiceCommand from "../VoiceCommand";
import ModuleMenuBar from "./ModuleMenuBar";

const BREADCRUMBS = {
  "/": "Apps", "/dashboard": "Dashboard", "/users": "Users", "/roles": "Roles & Access",
  "/company": "Company Profile", "/masters": "Master Data",
  "/purchase": "Purchase", "/purchase/create": "New Purchase Order",
  "/purchase/grn/create": "New GRN", "/production": "Production",
  "/inventory": "Inventory", "/quality": "Quality Control",
  "/sales": "Sales", "/planning": "Planning", "/finance": "Finance",
  "/maintenance": "Maintenance", "/hr": "HR & Payroll",
  "/security": "Security", "/documents": "Documents", "/reports": "Reports",
  "/training": "Training Guide",
};

export default function Header({ onMenuToggle, onShortcutsHelp }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { availableFYs, selectedFY, selectedLabel, changeFY } = useFY();
  const { companies, activeCompany, switchCompany } = useCompany();
  const [searchOpen, setSearchOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const fetchPendingCount = useCallback(async () => {
    try { const res = await api.get("/approvals/count/pending"); setPendingCount(res.data?.pending_count || 0); } catch {}
  }, []);
  useEffect(() => { fetchPendingCount(); const iv = setInterval(fetchPendingCount, 120000); return () => clearInterval(iv); }, [fetchPendingCount]);

  const isUUID = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
  const humanize = (s) => s.replace(/-/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());

  const pathParts = location.pathname.split("/").filter(Boolean);
  let breadcrumbs = [{ label: "Home", path: "/" }];
  if (pathParts.length > 0) {
    const currentPath = "/" + pathParts.join("/");
    const lastSegment = pathParts[pathParts.length - 1];
    // Skip UUID segments in breadcrumbs — detail pages already show their own title
    const label = BREADCRUMBS[currentPath] || (isUUID(lastSegment) ? null : humanize(lastSegment));
    if (pathParts.length > 1) {
      const parentPath = "/" + pathParts[0];
      breadcrumbs.push({ label: BREADCRUMBS[parentPath] || humanize(pathParts[0]), path: parentPath });
    }
    if (label) {
      breadcrumbs.push({ label, path: currentPath });
    }
  }

  return (
    <>
      <header
        data-testid="app-header"
        className="h-14 md:h-16 bg-card border-b flex items-center justify-between px-3 md:px-6 shrink-0"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Mobile hamburger */}
          <Button variant="ghost" size="icon" className="md:hidden h-9 w-9 rounded-md" onClick={onMenuToggle} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </Button>
          {/* Odoo-style module menu bar (desktop) */}
          <div className="hidden md:flex min-w-0 flex-1">
            <ModuleMenuBar />
          </div>
          {/* Mobile: show current page name only */}
          <span className="md:hidden text-sm font-semibold text-foreground truncate max-w-[140px]">
            {breadcrumbs[breadcrumbs.length - 1]?.label}
          </span>
        </div>

        <div className="flex items-center gap-1 md:gap-2">
          {/* Session database badge — which DB this login is connected to */}
          <SessionDbBadge />
          {/* Company Selector */}
          {companies.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="hidden sm:flex h-8 gap-1.5 rounded-md text-xs font-medium px-3" data-testid="company-selector-btn">
                  <Building2 className="w-3.5 h-3.5" />
                  {activeCompany?.short_code || "Company"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-md w-72">
                {companies.map(c => (
                  <DropdownMenuItem key={c.id} className={`rounded-lg text-sm flex items-center gap-2 ${activeCompany?.id === c.id ? "font-semibold bg-muted" : ""}`}
                    onClick={() => switchCompany(c.id)} data-testid={`company-option-${c.short_code}`}>
                    <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{c.short_code}</p>
                      <p className="text-[10px] text-muted-foreground">{c.name}</p>
                    </div>
                    {activeCompany?.id === c.id && <span className="ml-auto text-primary text-[10px]">Active</span>}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* FY Selector - hidden on small mobile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="hidden sm:flex h-8 gap-1.5 rounded-md text-xs font-medium px-3" data-testid="fy-selector-btn">
                <Calendar className="w-3.5 h-3.5" />
                FY {selectedLabel}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-md w-36">
              {availableFYs.map(fy => (
                <DropdownMenuItem key={fy.code} className={`rounded-lg text-sm ${selectedFY === fy.code ? "font-semibold bg-muted" : ""}`}
                  onClick={() => changeFY(fy.code)} data-testid={`fy-option-${fy.code}`}>
                  FY {fy.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="global-search-btn"
            className="flex items-center gap-2 h-9 px-2 md:px-3.5 rounded-md border bg-background hover:bg-muted/50 transition-colors text-sm text-muted-foreground w-9 md:w-56"
          >
            <Search className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden md:block flex-1 text-left text-xs">Search...</span>
            <kbd className="hidden lg:inline-flex items-center gap-0.5 rounded-md border bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground/70">
              <span className="text-xs">&#8984;</span>K
            </kbd>
          </button>

          <NotificationBell />

          <VoiceCommand />

          {onShortcutsHelp && (
            <Button variant="ghost" size="icon" className="hidden md:flex h-9 w-9 rounded-md" onClick={onShortcutsHelp} title="Keyboard Shortcuts (Alt+?)" data-testid="shortcuts-help-btn">
              <Keyboard className="w-4 h-4" />
            </Button>
          )}

          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-md relative" onClick={() => navigate("/approvals")} title="Pending Approvals" data-testid="approvals-btn">
            <ClipboardCheck className="w-4 h-4" />
            {pendingCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="pending-approval-count">{pendingCount > 99 ? "99+" : pendingCount}</span>
            )}
          </Button>

          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-btn" className="h-9 w-9 rounded-md">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 h-9 px-2 rounded-md" data-testid="user-menu-btn">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                  {user?.full_name?.charAt(0)?.toUpperCase() || "U"}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 rounded-md p-1.5">
              <div className="px-3 py-2">
                <p className="text-sm font-semibold">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground">{user?.role_name}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2 text-sm rounded-lg py-2" data-testid="profile-menu-item" onClick={() => navigate("/user-profile")}>
                <User className="w-4 h-4" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 text-sm rounded-lg py-2" data-testid="training-menu-item" onClick={() => navigate("/help?tab=training")}>
                <GraduationCap className="w-4 h-4" /> Training Guide
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2 text-sm text-destructive rounded-lg py-2" onClick={logout} data-testid="logout-menu-item">
                <LogOut className="w-4 h-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}

function SessionDbBadge() {
  const dbName = (() => {
    try {
      const token = localStorage.getItem("stancor_token");
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        if (payload.db) return payload.db;
      }
    } catch { /* fall through */ }
    return localStorage.getItem("stancor_db") || "";
  })();
  const label = localStorage.getItem("stancor_db_label") || dbName;

  if (!dbName) return null;
  return (
    <span
      className="hidden sm:inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-semibold tracking-wide select-none"
      title={`You are working in the "${label}" database (${dbName}) — all entries are saved here`}
      data-testid="session-db-badge"
    >
      <Database className="w-3.5 h-3.5" />
      {label}
    </span>
  );
}
